import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition, Switch } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { useGDPRStore, ConsentPreferences } from "../../stores/gdprStore";
import { Cookie, Shield, Settings, X, ChevronDown, ChevronUp, Lock, BarChart3, Megaphone, Sliders } from "lucide-react";

import { useLocation } from "react-router-dom";
import { useOnboardingStore } from "../../stores/onboardingStore";

/**
 * GDPR Consent Banner Component
 * 
 * A comprehensive, accessible, and visually appealing GDPR consent banner
 * that matches the Dashbal design system. Supports:
 * - Accept All / Reject All quick actions
 * - Detailed preferences modal with category toggles
 * - Smooth animations and transitions
 * - Full accessibility compliance
 * - Dark mode support
 */
export function GDPRBanner() {
    const { t } = useTranslation("common");
    const location = useLocation();
    const { isActive: isOnboardingActive } = useOnboardingStore();
    const {
        hasConsented,
        isBannerVisible,
        showBanner,
        acceptAll,
        rejectAll,
        openPreferencesModal,
        isPreferencesModalOpen
    } = useGDPRStore();

    const [isOtherModalActive, setIsOtherModalActive] = useState(false);

    // Effect to detect if any other modal is open on the page
    useEffect(() => {
        const checkModals = () => {
            // Check for Headless UI Dialogs (role="dialog")
            const dialogs = document.querySelectorAll('[role="dialog"]');
            let hasOtherDialog = false;
            
            dialogs.forEach(dialog => {
                // If the dialog is not our own preferences modal, it's an "other" modal
                if (!dialog.closest('.gdpr-preferences-modal')) {
                    hasOtherDialog = true;
                }
            });

            // Check for other common modal/drawer markers in the project
            const hasBackdrop = !!document.querySelector('.modal-backdrop:not(.gdpr-preferences-modal)');
            const hasDrawer = !!document.querySelector('.drawer-backdrop');
            
            // Also check for body overflow as a generic indicator 
            // but ignore it if only OUR preferences modal is responsible
            const isBodyLocked = document.body.style.overflow === 'hidden';
            // Headless UI adds data-headlessui-state="open" to the dialog's container usually, 
            // but checking for visible dialogs is more reliable.

            setIsOtherModalActive(hasOtherDialog || hasBackdrop || (isBodyLocked && !isPreferencesModalOpen) || hasDrawer);
        };

        // Poll for changes (more reliable than MutationObserver for complex React state shifts across portals)
        const interval = setInterval(checkModals, 500);
        
        // Also use MutationObserver for immediate response to DOM changes
        const observer = new MutationObserver(checkModals);
        observer.observe(document.body, { 
            childList: true, 
            subtree: true, 
            attributes: true, 
            attributeFilter: ['style', 'class', 'data-headlessui-state'] 
        });

        checkModals();

        return () => {
            clearInterval(interval);
            observer.disconnect();
        };
    }, [isPreferencesModalOpen]);

    // Show banner on mount if user hasn't consented yet
    useEffect(() => {
        if (!hasConsented) {
            // Small delay for better UX
            const timer = setTimeout(() => {
                showBanner();
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [hasConsented, showBanner]);

    const isLandingPage = location.pathname === "/";
    const shouldHide = isLandingPage || isOnboardingActive || isOtherModalActive;

    return (
        <>
            <Transition appear show={isBannerVisible && !shouldHide} as={Fragment}>
                <div className="fixed bottom-0 left-0 right-0 z-[100] p-4 sm:p-6 pointer-events-none">
                    <Transition.Child
                        as={Fragment}
                        enter="transform transition ease-out duration-500"
                        enterFrom="translate-y-full opacity-0"
                        enterTo="translate-y-0 opacity-100"
                        leave="transform transition ease-in duration-300"
                        leaveFrom="translate-y-0 opacity-100"
                        leaveTo="translate-y-full opacity-0"
                    >
                        <div className="pointer-events-auto mx-auto max-w-4xl">
                            {/* Glassmorphism card */}
                            <div className="relative overflow-hidden rounded-2xl bg-white/95 dark:bg-neutral-900/95 backdrop-blur-xl border border-neutral-200/50 dark:border-neutral-700/50 shadow-2xl">
                                {/* Decorative gradient accent */}
                                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-accent-primary via-amber-500 to-accent-primary" />

                                <div className="p-5 sm:p-6">
                                    {/* Header */}
                                    <div className="flex items-start gap-4">
                                        <div className="flex-shrink-0 p-2.5 bg-accent-primary/10 dark:bg-accent-primary/20 rounded-xl">
                                            <Cookie className="w-6 h-6 text-accent-primary" />
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-lg font-semibold text-text-primary dark:text-neutral-100 mb-1">
                                                {t("gdpr.banner.title")}
                                            </h3>
                                            <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                                                {t("gdpr.banner.description")}
                                            </p>

                                            {/* Privacy policy link */}
                                            <a
                                                href="/privacy"
                                                className="inline-flex items-center gap-1.5 text-sm text-accent-primary hover:text-accent-primary/80 transition-colors mt-2"
                                            >
                                                <Shield className="w-3.5 h-3.5" />
                                                {t("gdpr.banner.learnMore")}
                                            </a>
                                        </div>
                                    </div>

                                    {/* Action buttons */}
                                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-5">
                                        <button
                                            onClick={openPreferencesModal}
                                            className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-accent-primary focus:ring-offset-2 dark:focus:ring-offset-neutral-900"
                                        >
                                            <Settings className="w-4 h-4" />
                                            {t("gdpr.banner.customize")}
                                        </button>

                                        <div className="flex items-center gap-3 sm:ml-auto">
                                            <button
                                                onClick={rejectAll}
                                                className="flex-1 sm:flex-none px-5 py-2.5 text-sm font-medium text-neutral-700 dark:text-neutral-300 border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-800 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-accent-primary focus:ring-offset-2 dark:focus:ring-offset-neutral-900"
                                            >
                                                {t("gdpr.banner.rejectAll")}
                                            </button>

                                            <button
                                                onClick={acceptAll}
                                                className="flex-1 sm:flex-none px-5 py-2.5 text-sm font-medium text-white bg-accent-primary hover:bg-accent-primary/90 rounded-xl shadow-lg shadow-accent-primary/25 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-accent-primary focus:ring-offset-2 dark:focus:ring-offset-neutral-900"
                                            >
                                                {t("gdpr.banner.acceptAll")}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Transition.Child>
                </div>
            </Transition>

            {/* Preferences Modal */}
            <GDPRPreferencesModal />
        </>
    );
}

/**
 * GDPR Preferences Modal
 * 
 * Detailed cookie preferences modal with individual category toggles
 */
function GDPRPreferencesModal() {
    const { t } = useTranslation("common");
    const {
        isPreferencesModalOpen,
        closePreferencesModal,
        consentPreferences,
        savePreferences,
        acceptAll,
        rejectAll
    } = useGDPRStore();

    // Local state for preferences before saving
    const [localPreferences, setLocalPreferences] = useState<ConsentPreferences>({
        ...consentPreferences
    });

    // Track which categories are expanded
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

    // Reset local preferences when modal opens
    useEffect(() => {
        if (isPreferencesModalOpen) {
            setLocalPreferences({ ...consentPreferences });
        }
    }, [isPreferencesModalOpen, consentPreferences]);

    const toggleCategory = (category: keyof ConsentPreferences) => {
        if (category === "necessary") return; // Can't toggle necessary
        setLocalPreferences(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };

    const toggleExpanded = (category: string) => {
        setExpandedCategories(prev => {
            const next = new Set(prev);
            if (next.has(category)) {
                next.delete(category);
            } else {
                next.add(category);
            }
            return next;
        });
    };

    const handleSave = () => {
        savePreferences(localPreferences);
    };

    const categories = [
        {
            key: "necessary" as const,
            icon: Lock,
            required: true,
        },
        {
            key: "analytics" as const,
            icon: BarChart3,
            required: false,
        },
        {
            key: "marketing" as const,
            icon: Megaphone,
            required: false,
        },
        {
            key: "preferences" as const,
            icon: Sliders,
            required: false,
        },
    ];

    return (
        <Transition appear show={isPreferencesModalOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[110] gdpr-preferences-modal" onClose={closePreferencesModal}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white dark:bg-neutral-900 shadow-2xl transition-all border border-neutral-200/50 dark:border-neutral-700/50">
                                {/* Header */}
                                <div className="relative px-6 py-5 border-b border-neutral-200 dark:border-neutral-800">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-accent-primary/10 dark:bg-accent-primary/20 rounded-xl">
                                            <Cookie className="w-5 h-5 text-accent-primary" />
                                        </div>
                                        <Dialog.Title className="text-lg font-semibold text-text-primary dark:text-neutral-100">
                                            {t("gdpr.preferences.title")}
                                        </Dialog.Title>
                                    </div>
                                    <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
                                        {t("gdpr.preferences.description")}
                                    </p>
                                    <button
                                        onClick={closePreferencesModal}
                                        className="absolute right-4 top-4 p-2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>

                                {/* Categories */}
                                <div className="px-6 py-4 max-h-[50vh] overflow-y-auto space-y-3">
                                    {categories.map(({ key, icon: Icon, required }) => {
                                        const isExpanded = expandedCategories.has(key);
                                        const isEnabled = localPreferences[key];

                                        return (
                                            <div
                                                key={key}
                                                className="border border-neutral-200 dark:border-neutral-700 rounded-xl overflow-hidden transition-all duration-200"
                                            >
                                                <div className="flex items-center gap-3 p-4">
                                                    <div className={`p-2 rounded-lg ${isEnabled
                                                        ? "bg-accent-primary/10 text-accent-primary"
                                                        : "bg-neutral-100 dark:bg-neutral-800 text-neutral-500"
                                                        }`}>
                                                        <Icon className="w-4 h-4" />
                                                    </div>

                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-medium text-text-primary dark:text-neutral-100">
                                                                {t(`gdpr.categories.${key}.title`)}
                                                            </span>
                                                            {required && (
                                                                <span className="px-2 py-0.5 text-xs font-medium bg-accent-primary/10 text-accent-primary rounded-full">
                                                                    {t("gdpr.required")}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <button
                                                        onClick={() => toggleExpanded(key)}
                                                        className="p-1.5 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
                                                    >
                                                        {isExpanded ? (
                                                            <ChevronUp className="w-4 h-4" />
                                                        ) : (
                                                            <ChevronDown className="w-4 h-4" />
                                                        )}
                                                    </button>

                                                    <Switch
                                                        checked={isEnabled}
                                                        onChange={() => toggleCategory(key)}
                                                        disabled={required}
                                                        className={`${isEnabled ? "bg-accent-primary" : "bg-neutral-300 dark:bg-neutral-600"
                                                            } ${required ? "opacity-50 cursor-not-allowed" : ""} relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-accent-primary focus:ring-offset-2 dark:focus:ring-offset-neutral-900`}
                                                    >
                                                        <span
                                                            className={`${isEnabled ? "translate-x-6" : "translate-x-1"
                                                                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm`}
                                                        />
                                                    </Switch>
                                                </div>

                                                {/* Expanded details */}
                                                <Transition
                                                    show={isExpanded}
                                                    enter="transition-all duration-200 ease-out"
                                                    enterFrom="opacity-0 max-h-0"
                                                    enterTo="opacity-100 max-h-40"
                                                    leave="transition-all duration-150 ease-in"
                                                    leaveFrom="opacity-100 max-h-40"
                                                    leaveTo="opacity-0 max-h-0"
                                                >
                                                    <div className="px-4 pb-4 pt-0">
                                                        <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed pl-11">
                                                            {t(`gdpr.categories.${key}.description`)}
                                                        </p>
                                                    </div>
                                                </Transition>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Footer */}
                                <div className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-800/50">
                                    <div className="flex flex-col sm:flex-row gap-3">
                                        <button
                                            onClick={rejectAll}
                                            className="px-4 py-2.5 text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200 transition-colors"
                                        >
                                            {t("gdpr.banner.rejectAll")}
                                        </button>
                                        <div className="flex-1" />
                                        <button
                                            onClick={acceptAll}
                                            className="px-4 py-2.5 text-sm font-medium text-neutral-700 dark:text-neutral-300 border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-xl transition-colors"
                                        >
                                            {t("gdpr.banner.acceptAll")}
                                        </button>
                                        <button
                                            onClick={handleSave}
                                            className="px-5 py-2.5 text-sm font-medium text-white bg-accent-primary hover:bg-accent-primary/90 rounded-xl shadow-lg shadow-accent-primary/25 transition-all duration-200"
                                        >
                                            {t("gdpr.preferences.savePreferences")}
                                        </button>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}

export default GDPRBanner;
